import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import logoImage from "../../assets/images/logo.webp";
import { getProductImageUrl } from "../../utils/imageUrl";

const API_ROOT = "https://admin.monsinidress.com";
const NAVBAR_URL = `${API_ROOT}/api/navbar`;
const PRODUCTS_URL = `${API_ROOT}/api/products/brand`;
const DEFAULT_YEAR = 2026;

const MenuIcon = () => (
	<svg
		viewBox="0 0 24 24"
		width="28"
		height="28"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		aria-hidden="true">
		<path d="M4 6h16M4 12h16M4 18h16" />
	</svg>
);
const SearchIcon = () => (
	<svg
		viewBox="0 0 24 24"
		width="24"
		height="24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		aria-hidden="true">
		<circle cx="11" cy="11" r="7" />
		<path d="m20 20-4-4" />
	</svg>
);
const CloseIcon = () => (
	<svg
		viewBox="0 0 24 24"
		width="26"
		height="26"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		aria-hidden="true">
		<path d="M6 6l12 12M18 6 6 18" />
	</svg>
);
const ChevronIcon = ({ open = false }) => (
	<svg
		viewBox="0 0 20 20"
		width="16"
		height="16"
		fill="currentColor"
		className={`transition-transform ${open ? "rotate-180" : ""}`}
		aria-hidden="true">
		<path
			fillRule="evenodd"
			d="M5.22 7.97a.75.75 0 0 1 1.06 0L10 11.69l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.03a.75.75 0 0 1 0-1.06Z"
			clipRule="evenodd"
		/>
	</svg>
);
const InstagramIcon = () => (
	<svg
		viewBox="0 0 24 24"
		width="25"
		height="25"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		aria-hidden="true">
		<rect x="3" y="3" width="18" height="18" rx="5" />
		<circle cx="12" cy="12" r="4" />
		<circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
	</svg>
);

const DropdownMenu = memo(function DropdownMenu({
	title,
	options,
	onNeedData,
}) {
	const [open, setOpen] = useState(false);
	const menuId = `${title.toLowerCase()}-menu`;

	const openMenu = () => {
		onNeedData?.();
		setOpen(true);
	};

	return (
		<li
			className="relative font-poppins font-medium text-[15px]"
			onMouseEnter={openMenu}
			onMouseLeave={() => setOpen(false)}>
			<button
				type="button"
				onClick={() => {
					onNeedData?.();
					setOpen((value) => !value);
				}}
				onFocus={onNeedData}
				aria-expanded={open}
				aria-controls={menuId}
				className="flex items-center gap-1 py-2 px-3 text-black hover:text-[#8a459f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8a459f] rounded transition">
				{title}
				<ChevronIcon open={open} />
			</button>
			<div
				id={menuId}
				className={`absolute left-1/2 -translate-x-1/2 top-full w-52 rounded-md bg-white shadow-lg ring-1 ring-black/10 z-40 transition ${open ? "visible opacity-100" : "invisible opacity-0"}`}>
				{options.map((option) => (
					<Link
						key={option.value}
						to={option.value}
						aria-label={option.ariaLabel}
						onClick={() => setOpen(false)}
						className="block px-4 py-3 text-sm text-gray-800 hover:bg-gray-50 hover:text-[#713580] text-center border-b border-gray-100 last:border-b-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#8a459f]">
						{option.label}
					</Link>
				))}
			</div>
		</li>
	);
});

function Navbar() {
	const [showSearch, setShowSearch] = useState(false);
	const [showMenu, setShowMenu] = useState(false);
	const [mobileDropdown, setMobileDropdown] = useState(false);
	const [search, setSearch] = useState("");
	const [products, setProducts] = useState([]);
	const [searchLoading, setSearchLoading] = useState(false);
	const [navbarData, setNavbarData] = useState(null);
	const [navRequested, setNavRequested] = useState(false);

	const ensureNavbarData = useCallback(() => {
		setNavRequested(true);
	}, []);

	useEffect(() => {
		if (!navRequested || navbarData) return undefined;
		const controller = new AbortController();
		const timer = window.setTimeout(() => controller.abort(), 5000);

		(async () => {
			try {
				const response = await fetch(NAVBAR_URL, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ brand: "Risky" }),
					signal: controller.signal,
				});
				if (!response.ok) return;
				const result = await response.json();
				setNavbarData(result?.data || {});
			} catch (_) {
				// Fallback navigation stays available.
			} finally {
				window.clearTimeout(timer);
			}
		})();

		return () => {
			window.clearTimeout(timer);
			controller.abort();
		};
	}, [navRequested, navbarData]);

	useEffect(() => {
		if (!showSearch || products.length) return undefined;
		const controller = new AbortController();
		const timer = window.setTimeout(() => controller.abort(), 6000);
		setSearchLoading(true);

		(async () => {
			try {
				const response = await fetch(PRODUCTS_URL, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ brand: "Risky" }),
					signal: controller.signal,
				});
				if (!response.ok) return;
				const result = await response.json();
				setProducts(
					Array.isArray(result?.data?.products) ? result.data.products : [],
				);
			} catch (_) {
				// Search simply stays empty if the API is unavailable.
			} finally {
				window.clearTimeout(timer);
				setSearchLoading(false);
			}
		})();

		return () => {
			window.clearTimeout(timer);
			controller.abort();
		};
	}, [showSearch, products.length]);

	const gownsOptions = useMemo(() => {
		const years =
			Array.isArray(navbarData?.Gowns?.years) && navbarData.Gowns.years.length
				? [...new Set(navbarData.Gowns.years)].sort(
						(a, b) => Number(b) - Number(a),
					)
				: [DEFAULT_YEAR];
		return [
			...years.map((year) => ({
				label: `${year} Collection`,
				ariaLabel: `${year} Gowns Collection`,
				value: `/gowns/${year}`,
			})),
			{
				label: "Size Chart",
				ariaLabel: "Gowns Size Chart",
				value: "/size-chart/gowns",
			},
		];
	}, [navbarData]);

	const filteredData = useMemo(() => {
		const term = search.trim().toLowerCase();
		if (!term) return [];
		return products
			.filter((item) =>
				String(item?.style || "")
					.toLowerCase()
					.includes(term),
			)
			.slice(0, 8);
	}, [products, search]);

	const closeMobile = () => {
		setShowMenu(false);
		setMobileDropdown(false);
	};

	return (
		<>
			<a
				href="#main-content"
				className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-white focus:px-4 focus:py-2 focus:shadow">
				Skip to main content
			</a>
			<header
				className="fixed w-full top-0 z-50 bg-white border-b border-gray-100"
				aria-label="Site header">
				<div className="flex justify-between items-center bg-[#8a459f] px-5 py-3 text-white">
					<button
						type="button"
						onClick={() => setShowMenu(true)}
						className="lg:hidden p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
						aria-label="Open navigation menu">
						<MenuIcon />
					</button>
					<a
						href="https://www.instagram.com/riskydress?igsh=MXhhdXF1aHk0Z3ZmcA=="
						target="_blank"
						rel="noopener noreferrer"
						className="hidden md:flex items-center"
						aria-label="Risky Dress on Instagram">
						<InstagramIcon />
					</a>
					<button
						type="button"
						onClick={() => setShowSearch(true)}
						className="p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
						aria-label="Search products">
						<SearchIcon />
					</button>
				</div>

				<div className="w-full flex justify-center items-center py-3">
					<Link
						to="/"
						aria-label="Risky Dress home"
						className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8a459f]">
						<img src={logoImage} alt="Risky Dress" className="h-12 w-auto" />
					</Link>
				</div>

				<nav aria-label="Primary navigation">
					<ul className="hidden lg:flex font-poppins items-center justify-center gap-8 px-8 text-black font-medium text-base pb-3">
						<li>
							<Link
								to="/"
								className="hover:text-[#713580] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8a459f] rounded">
								Home
							</Link>
						</li>
						<DropdownMenu
							title="Gowns"
							options={gownsOptions}
							onNeedData={ensureNavbarData}
						/>
						<li>
							<Link
								to="/about"
								className="hover:text-[#713580] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8a459f] rounded">
								About Us
							</Link>
						</li>
						<li>
							<Link
								to="/contact"
								className="hover:text-[#713580] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8a459f] rounded">
								Contact Us
							</Link>
						</li>
						<li>
							<a
								href="https://market.riskydress.com/"
								target="_blank"
								rel="noopener noreferrer"
								className="block text-[#713580] hover:text-white bg-white hover:bg-[#713580] border-2 border-[#713580] px-3 py-2 rounded-md transition">
								<span className="text-sm font-medium">RETAILER LOGIN</span>
							</a>
						</li>
					</ul>
				</nav>
			</header>

			{showMenu && (
				<div
					className="fixed inset-0 z-[80] bg-black/50 lg:hidden"
					role="dialog"
					aria-modal="true"
					aria-label="Mobile navigation">
					<div className="h-full w-[86%] max-w-sm bg-white p-6 overflow-y-auto">
						<div className="flex items-center justify-between mb-8">
							<img src={logoImage} alt="Risky Dress" className="h-12 w-auto" />
							<button
								type="button"
								onClick={closeMobile}
								aria-label="Close navigation menu"
								className="p-2">
								<CloseIcon />
							</button>
						</div>
						<nav aria-label="Mobile navigation">
							<ul className="space-y-3 text-gray-900 font-poppins">
								<li>
									<Link to="/" onClick={closeMobile} className="block py-2">
										Home
									</Link>
								</li>
								<li>
									<button
										type="button"
										onClick={() => {
											ensureNavbarData();
											setMobileDropdown((v) => !v);
										}}
										aria-expanded={mobileDropdown}
										className="w-full flex justify-between items-center py-2">
										Gowns <ChevronIcon open={mobileDropdown} />
									</button>
									{mobileDropdown && (
										<div className="pl-4 space-y-1">
											{gownsOptions.map((option) => (
												<Link
													key={option.value}
													to={option.value}
													onClick={closeMobile}
													className="block py-2">
													{option.label}
												</Link>
											))}
										</div>
									)}
								</li>
								<li>
									<Link
										to="/about"
										onClick={closeMobile}
										className="block py-2">
										About Us
									</Link>
								</li>
								<li>
									<Link
										to="/contact"
										onClick={closeMobile}
										className="block py-2">
										Contact Us
									</Link>
								</li>
								<li>
									<a
										href="https://market.riskydress.com/"
										target="_blank"
										rel="noopener noreferrer"
										className="block py-2 text-[#713580] font-semibold">
										Retailer Login
									</a>
								</li>
							</ul>
						</nav>
					</div>
				</div>
			)}

			{showSearch && (
				<div
					className="fixed inset-0 z-[90] bg-black/60 flex items-start justify-center p-4 pt-24"
					role="dialog"
					aria-modal="true"
					aria-labelledby="search-title">
					<div className="w-full max-w-2xl bg-white rounded-xl shadow-2xl p-5 max-h-[80vh] overflow-y-auto">
						<div className="flex justify-between items-center gap-4 mb-4">
							<h2 id="search-title" className="text-xl font-semibold">
								Search products
							</h2>
							<button
								type="button"
								onClick={() => {
									setShowSearch(false);
									setSearch("");
								}}
								aria-label="Close search"
								className="p-2">
								<CloseIcon />
							</button>
						</div>
						<label htmlFor="product-search" className="sr-only">
							Search by style number
						</label>
						<input
							id="product-search"
							type="search"
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							autoFocus
							placeholder="Search by style number…"
							className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-[#8a459f]"
						/>
						<div className="mt-4">
							{searchLoading && (
								<p role="status" className="text-gray-600">
									Loading products…
								</p>
							)}
							{!searchLoading && search.trim() && filteredData.length === 0 && (
								<p className="text-gray-600">No results found.</p>
							)}
							{filteredData.map((item) => (
								<Link
									key={item._id || item.style}
									to={`/Gowns/id/${item.style}`}
									state={{ product: item }}
									onClick={() => setShowSearch(false)}
									className="flex items-center gap-3 py-3 border-b last:border-b-0 hover:bg-gray-50">
									{getProductImageUrl(item) ? (
										<img
											src={getProductImageUrl(item)}
											alt=""
											width="64"
											height="80"
											loading="lazy"
											decoding="async"
											className="w-16 h-20 object-cover object-top"
										/>
									) : null}
									<span className="font-medium">Style {item.style}</span>
								</Link>
							))}
						</div>
					</div>
				</div>
			)}
		</>
	);
}

export default memo(Navbar);
